import React from 'react'

export interface SegmentedControlOption<T extends string | boolean | null> {
  value: T
  label: string
  icon?: React.ReactNode
}

interface SegmentedControlProps<T extends string> {
  value: T
  options: Array<SegmentedControlOption<T>>
  onChange: (value: T) => void
  className?: string
}

const activeSegmentShadow =
  '0px 2px 4px 0px rgba(0,0,0,0.04), 0px 1px 2px -1px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const segmentClass = (isActive: boolean): string => {
  if (isActive) {
    return 'flex-1 self-stretch px-2 py-0.5 bg-Estigroup-primary rounded-sm flex justify-center items-center overflow-hidden'
  }

  return 'flex-1 self-stretch px-2 py-0.5 rounded-md flex justify-center items-center overflow-hidden'
}

const SegmentedControl = <T extends string>({
  value,
  options,
  onChange,
  className = 'w-72'
}: SegmentedControlProps<T>): JSX.Element => {
  return (
    <div
      className={`${className} self-stretch bg-white rounded-lg outline outline-1 outline-neutral-200 flex justify-start items-center overflow-hidden`}>
      {options.map((option) => {
        const isActive = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              onChange(option.value)
            }}
            className={segmentClass(isActive)}
            style={isActive ? { boxShadow: activeSegmentShadow } : {}}>
            <div
              className={`text-xs font-medium font-Montserrat leading-5 ${
                isActive ? 'text-foregrounds-fg-on-color' : 'text-foregrounds-fg-muted'
              }`}>
              {option.label}
            </div>
          </button>
        )
      })}
    </div>
  )
}

export default SegmentedControl
